import { lockScroll, unlockScroll } from '@theme/utilities';

(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const getFocusable = (root) => [...root.querySelectorAll(focusableSelector)].filter((element) => {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });

  const closeMegaMenus = (exceptNav = null, restoreFocus = false) => {
    document.querySelectorAll('[data-tbso-navigation][data-open]').forEach((nav) => {
      if (nav === exceptNav) return;
      const active = nav.querySelector('[data-tbso-mega-trigger][aria-expanded="true"]');
      nav.removeAttribute('data-open');
      nav.querySelectorAll('[data-tbso-mega-trigger]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      nav.querySelectorAll('[data-tbso-mega-panel]').forEach((panel) => {
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
      });
      if (restoreFocus) active?.focus();
    });
  };

  const initNavigation = (nav) => {
    if (!nav || nav.dataset.ready === 'true') return;
    nav.dataset.ready = 'true';
    const triggers = [...nav.querySelectorAll('[data-tbso-mega-trigger]')];
    const panels = [...nav.querySelectorAll('[data-tbso-mega-panel]')];

    const close = ({ restoreFocus = false } = {}) => {
      const active = triggers.find((button) => button.getAttribute('aria-expanded') === 'true');
      nav.removeAttribute('data-open');
      triggers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      panels.forEach((panel) => {
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
      });
      if (restoreFocus) active?.focus();
    };

    const open = (button) => {
      const key = button.dataset.tbsoMegaTrigger;
      closeMegaMenus(nav);
      close();
      const panel = nav.querySelector(`[data-tbso-mega-panel="${key}"]`);
      if (!panel) return;
      nav.dataset.open = key;
      button.setAttribute('aria-expanded', 'true');
      panel.id ||= `tbso-mega-panel-${key}`;
      button.setAttribute('aria-controls', panel.id);
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
    };

    triggers.forEach((button) => {
      const key = button.dataset.tbsoMegaTrigger;
      const panel = nav.querySelector(`[data-tbso-mega-panel="${key}"]`);
      if (panel) {
        panel.id ||= `tbso-mega-panel-${key}`;
        panel.setAttribute('aria-hidden', panel.hidden ? 'true' : 'false');
        button.setAttribute('aria-controls', panel.id);
      }
      button.addEventListener('click', () => {
        if (button.getAttribute('aria-expanded') === 'true') {
          const parentUrl = button.dataset.tbsoParentUrl;
          if (parentUrl) window.location.assign(parentUrl);
          return;
        }
        open(button);
      });
    });

    nav.querySelectorAll('[data-tbso-nav-close]').forEach((element) => {
      element.addEventListener('click', () => close({ restoreFocus: true }));
    });

    nav.addEventListener('keydown', (event) => {
      if (!nav.dataset.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close({ restoreFocus: true });
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(nav);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth < 990 && nav.dataset.open) close();
    }, { passive: true });
  };

  const initSupportDrawer = (drawer) => {
    if (!drawer || drawer.dataset.ready === 'true') return;
    drawer.dataset.ready = 'true';
    let opener = null;

    const close = ({ restoreFocus = true } = {}) => {
      if (drawer.getAttribute('aria-hidden') === 'true') return;
      drawer.removeAttribute('data-open');
      drawer.setAttribute('aria-hidden', 'true');
      unlockScroll(drawer);
      document.documentElement.removeAttribute('data-tbso-drawer-open');
      if (restoreFocus) opener?.focus?.();
    };

    const open = (event) => {
      opener = event?.currentTarget || document.activeElement;
      closeMegaMenus();
      drawer.dataset.open = 'true';
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll(drawer);
      document.documentElement.setAttribute('data-tbso-drawer-open', '');
      getFocusable(drawer)[0]?.focus();
    };

    document.querySelectorAll('[data-tbso-support-open]').forEach((element) => {
      if (element.dataset.tbsoSupportReady === 'true') return;
      element.dataset.tbsoSupportReady = 'true';
      element.setAttribute('aria-haspopup', 'dialog');
      element.setAttribute('aria-controls', drawer.id || 'TbsoSupportDrawer');
      element.addEventListener('click', open);
    });
    drawer.id ||= 'TbsoSupportDrawer';
    drawer.querySelectorAll('[data-tbso-support-close]').forEach((element) => element.addEventListener('click', () => close()));
    drawer.addEventListener('keydown', (event) => {
      if (drawer.getAttribute('aria-hidden') === 'true') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(drawer);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const init = () => {
    document.querySelectorAll('[data-tbso-navigation]').forEach(initNavigation);
    initSupportDrawer(document.querySelector('[data-tbso-support-drawer]'));
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
